import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import type { ScreenId } from '@/generated/screens.g'
import { screenLabel } from '@/router/screens'

/** The record an item screen has open: its id and the human name the screen shows for it. */
export interface ScreenItem {
  readonly id: string
  readonly label: string
}

export interface CurrentSurface {
  readonly screen: ScreenId
  readonly label: string
  readonly item?: ScreenItem
}

/** Where the client is right now: the catalogue screen and, on an item screen, the open record. */
export const useSurfaceStore = defineStore('surface', () => {
  const screen = ref<ScreenId | null>(null)
  const item = ref<ScreenItem | null>(null)
  const location = ref<string | null>(null)
  let itemOwner: symbol | null = null

  const current = computed<CurrentSurface | null>(() =>
    screen.value
      ? {
          screen: screen.value,
          label: screenLabel(screen.value),
          ...(item.value ? { item: item.value } : {}),
        }
      : null,
  )

  /** Records a navigation: sets the screen (`null` clears it) and drops the open item unless `location` is unchanged. */
  function enter(next: ScreenId | null, nextLocation: string): void {
    if (next === null || nextLocation !== location.value) {
      item.value = null
      itemOwner = null
    }
    screen.value = next
    location.value = nextLocation
  }

  /** Records the record the item screen has open on behalf of `owner`, or clears it with `null`. */
  function setItem(next: ScreenItem | null, owner: symbol): void {
    if (!next) {
      releaseItem(owner)
      return
    }
    item.value = next
    itemOwner = owner
  }

  /** Clears the open item if `owner` still holds it, so a screen that unmounts late cannot clear its successor's item. */
  function releaseItem(owner: symbol): void {
    if (itemOwner !== owner) return
    item.value = null
    itemOwner = null
  }

  return { current, enter, setItem, releaseItem }
})
